import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import { FIRM, NAV_LINKS, SERVICES } from "../lib/constants";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed. We'll be in touch with insights.");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#0B2E5C] text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Brand & newsletter */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src={FIRM.logo}
                alt="Mizan logo"
                className="h-12 w-12 bg-white p-1 rounded-sm"
              />
              <div>
                <div className="font-display text-2xl tracking-tight">Mizan</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#2BA8B8] font-semibold">
                  Audit & Assurance LLP
                </div>
              </div>
            </div>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed font-light">
              An independent audit & assurance practice in Mogadishu — built on
              precision, integrity and international standards.
            </p>

            <form
              onSubmit={onSubscribe}
              className="mt-8"
              data-testid="newsletter-form"
            >
              <label className="overline !text-[#2BA8B8]">Newsletter</label>
              <p className="mt-2 text-sm text-white/70 font-light">
                Quarterly insights on audit, IFRS and the Somali market.
              </p>
              <div className="mt-4 flex gap-2">
                <Input
                  type="email"
                  required
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-sm bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-[#2BA8B8] focus-visible:border-[#2BA8B8] h-11"
                  data-testid="newsletter-email-input"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit-button"
                  className="rounded-sm bg-[#2BA8B8] hover:bg-[#228592] text-white h-11 px-5"
                >
                  {loading ? "..." : <ArrowRight className="h-4 w-4" />}
                </Button>
              </div>
            </form>
          </div>

          {/* Sitemap */}
          <div className="lg:col-span-3">
            <div className="overline !text-[#2BA8B8]">Navigate</div>
            <ul className="mt-5 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-white/80 hover:text-white text-sm transition-colors link-underline"
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <div className="overline !text-[#2BA8B8]">Services</div>
            <ul className="mt-5 space-y-3">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link
                    to="/services"
                    className="text-white/80 hover:text-white text-sm transition-colors"
                    data-testid={`footer-service-${s.slug}`}
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-2">
            <div className="overline !text-[#2BA8B8]">Office</div>
            <ul className="mt-5 space-y-4 text-sm text-white/80 font-light">
              <li className="flex gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-[#2BA8B8] shrink-0" strokeWidth={1.5} />
                <span>
                  {FIRM.address}
                  <br />
                  {FIRM.city}
                </span>
              </li>
              <li className="flex gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-[#2BA8B8] shrink-0" strokeWidth={1.5} />
                <a href={FIRM.phoneHref} className="hover:text-white" data-testid="footer-phone-link">
                  {FIRM.phone}
                </a>
              </li>
              <li className="flex gap-3">
                <Mail className="h-4 w-4 mt-0.5 text-[#2BA8B8] shrink-0" strokeWidth={1.5} />
                <a href={FIRM.emailHref} className="hover:text-white" data-testid="footer-email-link">
                  {FIRM.email}
                </a>
              </li>
              <li className="flex gap-3">
                <Clock className="h-4 w-4 mt-0.5 text-[#2BA8B8] shrink-0" strokeWidth={1.5} />
                <span>{FIRM.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/15 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-white/60">
          <div data-testid="footer-copyright">
            © {new Date().getFullYear()} Mizan Audit & Assurance LLP. All rights reserved.
          </div>
          <div className="flex gap-6">
            <span>ISA‑compliant methodology</span>
            <span>IFRS / IPSAS aligned</span>
            <span>Mogadishu · Somalia</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
